/**
 * Convert OSGB36 British National Grid eastings/northings (EPSG:27700) to
 * WGS84 latitude/longitude (EPSG:4326) for display on a Leaflet map.
 *
 * Standard Ordnance Survey transformation: reverse the Transverse Mercator
 * projection on the Airy 1830 ellipsoid, then a Helmert datum shift to WGS84.
 * Accuracy is a few metres, which is fine for showing a site's restricted-area
 * boundary polygon.
 *
 * Reference: OS "A guide to coordinate systems in Great Britain".
 */

// Airy 1830 ellipsoid (OSGB36) and National Grid true-origin constants.
const a = 6377563.396      // semi-major axis
const b = 6356256.909      // semi-minor axis
const F0 = 0.9996012717    // central meridian scale factor
const lat0 = 49 * Math.PI / 180   // true origin latitude
const lon0 = -2 * Math.PI / 180   // true origin longitude
const N0 = -100000         // northing of true origin
const E0 = 400000          // easting of true origin
const e2 = 1 - (b * b) / (a * a)  // eccentricity squared
const n = (a - b) / (a + b)

/**
 * @param {number} E easting (metres)
 * @param {number} N northing (metres)
 * @returns {[number, number]} [lat, lng] in WGS84 degrees
 */
export function osgbToLatLng(E, N) {
  let lat = lat0
  let M = 0
  do {
    lat = (N - N0 - M) / (a * F0) + lat
    const Ma = (1 + n + (5 / 4) * n * n + (5 / 4) * n * n * n) * (lat - lat0)
    const Mb = (3 * n + 3 * n * n + (21 / 8) * n * n * n) * Math.sin(lat - lat0) * Math.cos(lat + lat0)
    const Mc = ((15 / 8) * n * n + (15 / 8) * n * n * n) * Math.sin(2 * (lat - lat0)) * Math.cos(2 * (lat + lat0))
    const Md = (35 / 24) * n * n * n * Math.sin(3 * (lat - lat0)) * Math.cos(3 * (lat + lat0))
    M = b * F0 * (Ma - Mb + Mc - Md)
  } while (Math.abs(N - N0 - M) >= 0.00001)

  const cosLat = Math.cos(lat)
  const sinLat = Math.sin(lat)
  const nu = a * F0 / Math.sqrt(1 - e2 * sinLat * sinLat)              // transverse radius of curvature
  const rho = a * F0 * (1 - e2) / Math.pow(1 - e2 * sinLat * sinLat, 1.5) // meridional radius
  const eta2 = nu / rho - 1

  const tanLat = Math.tan(lat)
  const tan2 = tanLat * tanLat
  const tan4 = tan2 * tan2
  const secLat = 1 / cosLat

  const VII = tanLat / (2 * rho * nu)
  const VIII = tanLat / (24 * rho * nu ** 3) * (5 + 3 * tan2 + eta2 - 9 * tan2 * eta2)
  const IX = tanLat / (720 * rho * nu ** 5) * (61 + 90 * tan2 + 45 * tan4)
  const X = secLat / nu
  const XI = secLat / (6 * nu ** 3) * (nu / rho + 2 * tan2)
  const XII = secLat / (120 * nu ** 5) * (5 + 28 * tan2 + 24 * tan4)
  const XIIA = secLat / (5040 * nu ** 7) * (61 + 662 * tan2 + 1320 * tan4 + 720 * tan2 * tan4)

  const dE = E - E0
  const latOSGB = lat - VII * dE ** 2 + VIII * dE ** 4 - IX * dE ** 6
  const lonOSGB = lon0 + X * dE - XI * dE ** 3 + XII * dE ** 5 - XIIA * dE ** 7

  // Helmert transform OSGB36 -> WGS84.
  return helmertOsgb36ToWgs84(latOSGB, lonOSGB)
}

function helmertOsgb36ToWgs84(lat, lon) {
  // OSGB36 -> WGS84 Helmert parameters (metres / ppm / arc-seconds).
  const tx = 446.448, ty = -125.157, tz = 542.06
  const s = -20.4894e-6
  const rx = (0.1502 / 3600) * Math.PI / 180
  const ry = (0.247 / 3600) * Math.PI / 180
  const rz = (0.8421 / 3600) * Math.PI / 180

  // Airy 1830 ellipsoid
  const a1 = 6377563.396, b1 = 6356256.909
  const e2a = 1 - (b1 * b1) / (a1 * a1)
  const nu1 = a1 / Math.sqrt(1 - e2a * Math.sin(lat) ** 2)

  const x1 = nu1 * Math.cos(lat) * Math.cos(lon)
  const y1 = nu1 * Math.cos(lat) * Math.sin(lon)
  const z1 = (1 - e2a) * nu1 * Math.sin(lat)

  const x2 = tx + (1 + s) * x1 + (-rz) * y1 + ry * z1
  const y2 = ty + rz * x1 + (1 + s) * y1 + (-rx) * z1
  const z2 = tz + (-ry) * x1 + rx * y1 + (1 + s) * z1

  // WGS84 ellipsoid
  const a2 = 6378137.0, b2 = 6356752.3142
  const e2b = 1 - (b2 * b2) / (a2 * a2)
  const p = Math.sqrt(x2 * x2 + y2 * y2)
  let latW = Math.atan2(z2, p * (1 - e2b))
  let latPrev
  do {
    latPrev = latW
    const nu2 = a2 / Math.sqrt(1 - e2b * Math.sin(latW) ** 2)
    latW = Math.atan2(z2 + e2b * nu2 * Math.sin(latW), p)
  } while (Math.abs(latW - latPrev) >= 1e-11)
  const lonW = Math.atan2(y2, x2)

  return [latW * 180 / Math.PI, lonW * 180 / Math.PI]
}

/**
 * Parse a restricted-area coordinate string ("E,N;E,N;...") of OSGB grid
 * eastings/northings into an array of [lat, lng] points for a Leaflet Polygon.
 * Returns null if the string doesn't parse into a usable ring (>= 3 points).
 */
export function parseRestrictedArea(str) {
  if (!str || typeof str !== 'string') return null
  const points = str
    .split(';')
    .map((pair) => pair.trim())
    .filter(Boolean)
    .map((pair) => {
      const [e, nrt] = pair.split(',').map((v) => parseFloat(v.trim()))
      if (!Number.isFinite(e) || !Number.isFinite(nrt)) return null
      // Sanity check: GB eastings 0-700000, northings 0-1300000.
      if (e < 0 || e > 700000 || nrt < 0 || nrt > 1300000) return null
      return osgbToLatLng(e, nrt)
    })
    .filter(Boolean)

  return points.length >= 3 ? points : null
}
